import getSymbolFromCurrency from "currency-symbol-map";

/**
 * The body the tenant Add/Edit modal sends.
 *
 * Lifted out of the page so the two shapes can be read side by side and
 * tested. They are genuinely different requests: `POST /admin/tenants`
 * provisions an organisation *and* invites its first admin, and
 * `PATCH /admin/tenants/{id}` edits the organisation only.
 *
 * `adminName` / `adminEmail` are create-only for that reason.
 * `UpdateTenantRequest` does not declare them and the base model ignores
 * extras, so an edit that sent them got a 200 and a "Tenant updated" toast
 * while the admin row was untouched. The modal already renders both fields
 * read-only on edit — they are there to show who the admin is — so nothing
 * was ever editable; the payload was simply claiming to write two fields the
 * server drops. Changing a tenant admin's identity is the invite flow's job,
 * and it has consequences (login identity, a fresh invitation) that a
 * silently-ignored field would not have covered anyway.
 */
/**
 * The country document sets a CREATE request carries, given the module plan.
 *
 * The form keeps a SuperAdmin's ticks even while the control is locked —
 * greying a control never rewrites the state behind it — so the selection can
 * read "IN, GB" while Compliance is unticked. This is where that combination is
 * resolved.
 *
 * It resolves to none. A country set is a compliance checklist, and seeding one
 * for a tenant without the module writes rows whose every `/api/v2/compliance/*`
 * route answers 403 MODULE_NOT_ENABLED: a checklist that exists and nobody can
 * open. The server refuses the same combination (`CreateTenantRequest`), so
 * this is the page agreeing with the route rather than the only thing stopping
 * it.
 *
 * `[]`, never `undefined`. The server reads the two differently — `None` is
 * "not being changed", `[]` is "no country masters for this tenant" — and only
 * the second is the answer being given here.
 *
 * Create only, deliberately. On Edit the same `[]` would revoke sets the tenant
 * already owns and may have personalised for months; unticking a module the
 * SuperAdmin only meant to look at must not do that.
 */
export const countryMastersForCreate = (selectedCodes, complianceSelected) => {
  if (!complianceSelected) return [];
  return selectedCodes || [];
};

export const buildTenantPayload = (formData, { mode, editingTenant } = {}) => {
  const isEdit = mode === "edit";
  const currency = formData.currency || editingTenant?.currency || "";

  // An unchanged slug is left out of an edit: `slug: null` means "don't touch"
  // to the server, and sending the current value would put a tenant created
  // under the older, looser slug rule (two or more hyphens) through today's
  // stricter one, so every other field on that tenant became unsaveable.
  const slugUnchanged = isEdit && formData.slug === editingTenant?.slug;

  const payload = {
    name: formData.name,
    ...(slugUnchanged ? {} : { slug: formData.slug }),
    region: formData.region,
    currency,
    currency_sign: getSymbolFromCurrency(currency) || "",
    // The seat licence, and only where it is one. Every invite route is shut
    // for an AD tenant (`require_non_ad_tenant`), so `max_users` caps nothing
    // there — and the sign-in method is fixed at creation, so it never starts
    // to. Omitted rather than sent as a number nobody enforces.
    ...(formData.adLoginEnabled ? {} : { maxUsers: formData.maxUsers }),
    // How many named status flows the tenant may define. A licence like
    // maxUsers, but one every tenant has — an AD tenant configures flows the
    // same as any other.
    maxCandidateStatusFlows: formData.maxCandidateStatusFlows,
    enabledExtensionSites: formData.enabledExtensionSites || [],
    complianceCountryMasters: formData.complianceCountryMasters || [],
    sourcingUsesOwnApiKeys: !!formData.sourcingUsesOwnApiKeys,
  };

  if (!isEdit) {
    // Create-only for the same reason: the sign-in method is fixed at
    // creation, so an edit carries neither the flag nor the first admin the
    // flag decides the shape of. The route refuses either mismatch — an
    // employee ID on an email tenant, a missing one on an AD tenant — so
    // exactly one identity is ever sent.
    payload.adLoginEnabled = !!formData.adLoginEnabled;
    if (formData.adLoginEnabled) {
      payload.adminEmployeeId = formData.adminEmployeeId;
    } else {
      payload.adminName = formData.adminName;
      payload.adminEmail = formData.adminEmail;
    }
  }

  return payload;
};

export default buildTenantPayload;
